import { Button, Tag, Space, Divider, Collapse } from 'antd';
import { CameraOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import React, { useEffect, useState } from 'react';
import EXIFTable from './EXIFTable';
import './PhotoFocus.css';

const PhotoFocus = ({ photoIndex, photoSet, setFocus, setNext, setPrev }) => {
    const [isHorizontal, setIsHorizontal] = useState(window.innerWidth > 768);
    const [hoverZone, setHoverZone] = useState(null); // 'left', 'right', or null
    const [direction, setDirection] = useState(null); // -1 or +1 for animation purposes
    const isAnimating = direction !== null;

    const wrapIndex = (i) => (i + photoSet.length) % photoSet.length;

    const photo = (photoSet && photoIndex !== null && photoIndex >= 0 && photoIndex < photoSet.length) ? photoSet[photoIndex] : null;

    const goPrev = () => {
        if (direction !== null) return; // Prevent rapid clicks
        setDirection(-1);
    }
    
    const goNext = () => {
        if (direction !== null) return; // Prevent rapid clicks
        setDirection(1);
    }


    // Handle left/right arrow keys for navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'Escape') setFocus(null);
        }
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [goNext, goPrev, setFocus]);

    // Handle browser back button
    useEffect(() => {
        window.history.pushState({ photofocus: true }, '');
        
        const handlePopState = () => {
            setFocus(null);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [setFocus]);

    // Handle window resize to switch between horizontal and vertical layouts
    useEffect(() => {
        const handleResize = () => {
            setIsHorizontal(window.innerWidth > 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle animation completion
    useEffect(() => {
      if (direction === null) return;

      const timer = setTimeout(() => {
        if (direction === 1) {
          setNext();
        } else if (direction === -1) {
          setPrev();
        }
        setDirection(null); // 🔑 snap back to center
      }, 300); // must match CSS transition

      return () => clearTimeout(timer);
    }, [direction, setNext, setPrev]);

    // Preload adjacent images
    useEffect(() => {
        if (!photoSet || photoIndex === null) return;

        const preloadImage = (index) => {
            const photo = photoSet[index];
            if (photo) {
                const img = new Image();
                img.src = photo.image_medres;
            }
        };

        preloadImage((photoIndex + 1) % photoSet.length);
        preloadImage((photoIndex - 1 + photoSet.length) % photoSet.length);
    }, [photoIndex, photoSet]);

    const handleBack = () => {
        window.history.back();
    };

    const handleImageClick = (e) => {
      const bounds = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const third = bounds.width / 3;


      if (x < third) {
        goPrev();
      } else if (x > third * 2) {
        goNext();
      }
      // middle third: do nothing
    };

    const handleMouseMove = (e) => {
      const bounds = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - bounds.left;
      const third = bounds.width / 3;

      if (x < third) {
        setHoverZone('left');
      } else if (x > third * 2) {
        setHoverZone('right');
      } else {
        setHoverZone(null);
      }
    };


    if (photo === null) {
        return (
            <h1>PhotoFocus was not given a photo!</h1>
        )
    }

    const JSXImageSection = (
        <div style={{
                    flex: '4',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'auto',
                    padding: '20px',
                    backgroundColor: 'transparent',
                    position: 'relative',
        }}>
                                    {/* Nav button overlay */}
                <Button
                    type="text"
                    icon={<LeftOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        goPrev();
                    }}
                    style={{
                        position: 'absolute',
                        left: '16px',
                        top: '50%',
                        zIndex: 10,
                        transform: `translateY(-50%) scale(${hoverZone === 'left' ? 1.2 : 1.0})`,
                        color: `rgba(255, 255, 255, ${hoverZone === 'left' ? 0.8 : 0.6})`,
                        mixBlendMode: 'difference',
                        fontSize: '20px',
                    }}
                    size="large"
                />
                <Button
                    type="text"
                    icon={<RightOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        goNext();
                    }}
                    style={{
                        position: 'absolute',
                        right: '16px',
                        top: '50%',
                        transform: `translateY(-50%) scale(${hoverZone === 'right' ? 1.2 : 1.0})`,
                        zIndex: 10,
                        color: `rgba(255, 255, 255, ${hoverZone === 'right' ? 0.8 : 0.6})`,
                        mixBlendMode: 'difference',
                        fontSize: '20px'
                    }}
                    size="large"
                />
                <div className="slider-viewport">
                  <div
                    className="slider-track"
                    style={{
                      transform: `translateX(${
                        direction === 1 
                            ? '-200%' 
                            : direction === -1 
                            ? '0%' 
                            : '-100%'
                      })`,
                      transition: isAnimating ? 'transform 0.3s ease-out' : 'none',
                    }}
                  >
                    {[photoIndex - 1, photoIndex, photoIndex + 1].map((rawIndex) => {
                      const i = wrapIndex(rawIndex);
                      const photo = photoSet[i];
                    
                      return (
                        <img
                          key={photo.id}
                          src={photo.image_medres}
                          className="slider-image"
                          alt=""
                          draggable={false}
                          onClick={handleImageClick}
                          onMouseMove={handleMouseMove}
                          onMouseLeave={() => setHoverZone(null)}
                        />
                      );
                    })}
                  </div>
                </div>
                </div>
    )

    // Metadata panel content
    const metadataContent = (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Subject */}
            <div>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: 'bold' }}>
                    SUBJECT
                </div>
                <div style={{ fontSize: '16px', color: '#ffffff' }}>
                    {photo.subject.name}
                </div>
            </div>

            {/* Tags */}
            {photo.tags && photo.tags.length > 0 && (
                <div>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', fontWeight: 'bold' }}>
                        TAGS
                    </div>
                    <Space wrap>
                        {photo.tags.map(tag => (
                            <Tag key={tag.id} color="#30305080" bordered={true}>
                                {tag.name}
                            </Tag>
                        ))}
                    </Space>
                </div>
            )}

            {/* Location */}
            {photo.location && (
                <div>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: 'bold' }}>
                        LOCATION
                    </div>
                    <div style={{ fontSize: '14px', color: '#ffffff' }}>
                        {photo.location.name}
                    </div>
                </div>
            )}

            {/* Caption */}
            {photo.caption && (
                <div>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px', fontWeight: 'bold' }}>
                        CAPTION
                    </div>
                    <div style={{ fontSize: '14px', color: '#ffffff' }}>
                        {photo.caption}
                    </div>
                </div>
            )}

            <Divider style={{ margin: '12px 0', borderColor: '#333' }} />

            {/* Camera Info */}
            <div style={{ color: '#ffffff' }}>
                <Collapse
                    items={[
                        {
                            key: '1',
                            label: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ffffff' }}>
                                    <CameraOutlined />
                                    <span>Camera Settings</span>
                                </div>
                            ),
                            children: <EXIFTable photo={photo} />,
                        },
                    ]}
                    style={{
                        backgroundColor: 'transparent',
                        borderColor: '#888'
                    }}
                />
            </div>
        </Space>
    );

    // Horizontal layout (image on left, metadata on right)
    if (isHorizontal) {
        return (
            <div style={{
                display: 'flex',
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                position: 'absolute',
                top: 0,
                left: 0
            }}>
                {/* Back button overlay */}
                <Button
                    type="text"
                    icon={<FullscreenExitOutlined />}
                    onClick={handleBack}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                        zIndex: 10,
                        color: '#ffffff',
                        fontSize: '18px',
                    }}
                    size="large"
                />

                {/* Image section */}
                {JSXImageSection}
 
                {/* Metadata section */}
                <div style={{
                    flex: '1',
                    backgroundColor: 'rgba(30, 30, 30, 0.7)',
                    overflowY: 'auto',
                    padding: '16px',
                    borderLeft: '1px solid #444',
                    backdropFilter: 'blur(10px)'
                }}>
                    {metadataContent}
                </div>
            </div>
        );
    }

    // Vertical layout (image on top, metadata below)
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            overflow: 'auto',
            position: 'absolute',
            top: 0,
            left: 0
        }}>
            {/* Back button overlay */}
            <Button
                type="text"
                icon={<FullscreenExitOutlined />}
                onClick={handleBack}
                style={{
                    position: 'absolute',
                    top: '16px',
                    left: '16px',
                    zIndex: 10,
                    color: '#ffffff',
                    fontSize: '18px'
                }}
                size="large"
            />

            {/* Image section */}
            {JSXImageSection}

            {/* Metadata section */}
            <div style={{
                flex: '1',
                backgroundColor: 'rgba(30, 30, 30, 0.7)',
                overflowY: 'auto',
                padding: '16px',
                color: '#ffffff',
                borderTop: '1px solid #444',
                backdropFilter: 'blur(10px)'
            }}>
                {metadataContent}
            </div>
        </div>
    );
}

export default PhotoFocus;
